import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import {
  FaChevronUp,
  FaTimes,
  FaComments,
  FaGripVertical,
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import FloatingChat from '../FloatingChat';

const HubButton = styled(motion.button)`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${({ theme }) => theme.primary};
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  z-index: 1001;
  box-shadow: 0 4px 20px rgba(133, 76, 230, 0.45);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 24px rgba(133, 76, 230, 0.55);
  }

  @media (max-width: 768px) {
    bottom: 20px;
    right: 20px;
    width: 52px;
    height: 52px;
  }
`;

const MenuContainer = styled(motion.div)`
  position: fixed;
  bottom: 100px;
  right: 30px;
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.primary + '30'};
  border-radius: 16px;
  padding: 16px;
  z-index: 1001;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  min-width: 220px;
  max-height: min(420px, calc(100vh - 160px));
  overflow-y: auto;

  @media (max-width: 768px) {
    bottom: 88px;
    right: 20px;
    min-width: 200px;
  }
`;

const MenuTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
  margin-bottom: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${({ theme }) => theme.text_secondary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 6px;

  &:hover {
    color: ${({ theme }) => theme.primary};
    background: ${({ theme }) => theme.primary + '15'};
  }
`;

const MenuItem = styled.a`
  display: block;
  padding: 10px 12px;
  color: ${({ theme }) => theme.text_secondary};
  text-decoration: none;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.primary + '20'};
    color: ${({ theme }) => theme.primary};
    transform: translateX(4px);
  }

  &.active {
    background: ${({ theme }) => theme.primary + '20'};
    color: ${({ theme }) => theme.primary};
    font-weight: 600;
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  margin-top: 4px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.text_secondary};
  font-size: 14px;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.primary + '20'};
    color: ${({ theme }) => theme.primary};
  }

  svg {
    font-size: 16px;
    flex-shrink: 0;
  }
`;

const MenuDivider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.primary + '25'};
  margin: 10px 0;
`;

const getSections = (t) => [
  { id: 'about', labelKey: 'nav.about' },
  { id: 'skills', labelKey: 'nav.skills' },
  { id: 'experience', labelKey: 'nav.experience' },
  { id: 'projects', labelKey: 'nav.projects' },
  { id: 'social-proof', labelKey: 'nav.socialProof' },
  { id: 'blog', labelKey: 'nav.blog' },
  { id: 'education', labelKey: 'nav.education' },
  { id: 'learning', labelKey: 'nav.learning' },
  { id: 'faq', labelKey: 'nav.faq' },
  { id: 'contact', labelKey: 'nav.contact' },
];

const ActionHub = () => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const sections = useMemo(() => getSections(t), [t]);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const scrollPosition = window.scrollY + 200;
        setShowBackToTop(window.scrollY > 500);

        for (const section of sections) {
          const element = document.getElementById(section.id);
          if (element) {
            const { offsetTop, offsetHeight } = element;
            if (
              scrollPosition >= offsetTop &&
              scrollPosition < offsetTop + offsetHeight
            ) {
              setActiveSection(section.id);
              break;
            }
          }
        }
        ticking = false;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollToSection = useCallback((id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMenuOpen(false);
    }
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setMenuOpen(false);
  };

  const openChat = () => {
    setMenuOpen(false);
    setChatOpen(true);
  };

  return (
    <>
      <FloatingChat
        hideLauncher
        isOpen={chatOpen}
        onOpenChange={setChatOpen}
      />

      <AnimatePresence>
        {menuOpen && (
          <MenuContainer
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
            role="menu"
            aria-label={t('actionHub.menuTitle')}
          >
            <MenuTitle>
              {t('actionHub.menuTitle')}
              <CloseButton
                type="button"
                onClick={() => setMenuOpen(false)}
                aria-label={t('actionHub.closeMenu')}
              >
                <FaTimes size={14} />
              </CloseButton>
            </MenuTitle>
            {sections.map((section) => (
              <MenuItem
                key={section.id}
                href={`#${section.id}`}
                className={activeSection === section.id ? 'active' : ''}
                role="menuitem"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(section.id);
                }}
              >
                {t(section.labelKey)}
              </MenuItem>
            ))}
            <MenuDivider />
            <ActionButton type="button" onClick={openChat} role="menuitem">
              <FaComments />
              {t('actionHub.openChat')}
            </ActionButton>
            {showBackToTop && (
              <ActionButton type="button" onClick={scrollToTop} role="menuitem">
                <FaChevronUp />
                {t('actionHub.backToTop')}
              </ActionButton>
            )}
          </MenuContainer>
        )}
      </AnimatePresence>

      <HubButton
        onClick={() => {
          if (chatOpen) {
            setChatOpen(false);
          } else {
            setMenuOpen((prev) => !prev);
          }
        }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.92 }}
        aria-label={
          menuOpen || chatOpen
            ? t('actionHub.closeMenu')
            : t('actionHub.openMenu')
        }
        aria-expanded={menuOpen || chatOpen}
      >
        {menuOpen || chatOpen ? <FaTimes /> : <FaGripVertical />}
      </HubButton>
    </>
  );
};

export default ActionHub;
